import { useState, useEffect, useRef } from 'react'
import { salesforceClient } from '../../lib/salesforce/client'
import { supabase } from '../../lib/supabase'
import { PhoneNumber } from '../phone/PhoneNumber'
import clsx from 'clsx'

export interface SelectedCustomer {
  accountId: string | null
  name: string
  email: string
  phone: string
  street: string
  postalCode: string
  city: string
  isNew: boolean
}

interface AccountResult {
  Id: string
  Name: string
  PersonEmail: string | null
  Phone: string | null
  BillingStreet: string | null
  BillingPostalCode: string | null
  BillingCity: string | null
}

/** Resultaatvorm van /api/customers/search (mirror-first); leads vallen af. */
interface ZoekResultaat {
  id: string
  type: 'account_person' | 'account_business' | 'lead'
  name: string
  email: string
  phone: string
  street?: string
  city: string
  postcode: string
}

/**
 * Klantzoeker op de mirror via /api/customers/search. Tot 18-09-2026 liep dit
 * als twee SOQL's over /api/sf-proxy, dood sinds de cut. Zelfde endpoint als
 * de klantenpagina; een WB-nummer wordt daar ook al herkend.
 */
async function zoekAccounts(query: string): Promise<AccountResult[]> {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  const res = await fetch(`/api/customers/search?q=${encodeURIComponent(query)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error(`Klantzoeker: ${res.status}`)
  const data = (await res.json()) as { results?: ZoekResultaat[] }
  return (data.results || [])
    .filter((r) => r.type !== 'lead')
    .slice(0, 10)
    .map((r) => ({
      Id: r.id,
      Name: r.name,
      PersonEmail: r.email || null,
      Phone: r.phone || null,
      BillingStreet: r.street || null,
      BillingPostalCode: r.postcode || null,
      BillingCity: r.city || null,
    }))
}

interface Props {
  value: SelectedCustomer | null
  onChange: (customer: SelectedCustomer | null) => void
}

interface PotentialMatch {
  id: string
  name: string
  email: string
  phone: string
  city: string
}

export function CustomerSearch({ value, onChange }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AccountResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [newCustomer, setNewCustomer] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    street: '', postalCode: '', city: '',
  })
  const [potentialMatch, setPotentialMatch] = useState<PotentialMatch | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const matchDebounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        setResults(await zoekAccounts(query.trim()))
      } catch (err) {
        console.error('Search error:', err)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(debounceRef.current)
  }, [query])

  // Pro-actieve duplicaat-detectie tijdens nieuwe-klant-invoer.
  // Triggert zodra phone ≥ 9 cijfers heeft of email-formaat klopt; toont banner
  // met "Gebruik deze klant"-knop. Blokkeert niet — medewerker mag altijd doorgaan.
  useEffect(() => {
    if (!showNewForm) {
      setPotentialMatch(null)
      return
    }
    const phoneClean = newCustomer.phone.replace(/[^\d]/g, '')
    const hasEnoughPhone = phoneClean.length >= 9
    const hasEnoughEmail = /\S+@\S+\.\S+/.test(newCustomer.email)
    if (!hasEnoughPhone && !hasEnoughEmail) {
      setPotentialMatch(null)
      return
    }

    clearTimeout(matchDebounceRef.current)
    matchDebounceRef.current = setTimeout(async () => {
      try {
        const result = await salesforceClient.matchCustomer({
          email: hasEnoughEmail ? newCustomer.email : undefined,
          phone: hasEnoughPhone ? newCustomer.phone : undefined,
        })
        setPotentialMatch(result)
      } catch (err) {
        console.error('Match check error:', err)
        setPotentialMatch(null)
      }
    }, 400)

    return () => clearTimeout(matchDebounceRef.current)
  }, [newCustomer.phone, newCustomer.email, showNewForm])

  const useExistingMatch = () => {
    if (!potentialMatch) return
    onChange({
      accountId: potentialMatch.id,
      name: potentialMatch.name,
      email: potentialMatch.email || newCustomer.email,
      phone: potentialMatch.phone || newCustomer.phone,
      street: newCustomer.street,
      postalCode: newCustomer.postalCode,
      city: newCustomer.city,
      isNew: false,
    })
    setShowNewForm(false)
    setPotentialMatch(null)
  }

  const selectAccount = (account: AccountResult) => {
    onChange({
      accountId: account.Id,
      name: account.Name,
      email: account.PersonEmail || '',
      phone: account.Phone || '',
      street: account.BillingStreet || '',
      postalCode: account.BillingPostalCode || '',
      city: account.BillingCity || '',
      isNew: false,
    })
    setQuery('')
    setResults([])
  }

  const submitNewCustomer = () => {
    if (!newCustomer.lastName) return
    onChange({
      accountId: null,
      name: `${newCustomer.firstName} ${newCustomer.lastName}`.trim(),
      email: newCustomer.email,
      phone: newCustomer.phone,
      street: newCustomer.street,
      postalCode: newCustomer.postalCode,
      city: newCustomer.city,
      isNew: true,
    })
    setShowNewForm(false)
  }

  // Selected customer display
  if (value) {
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div>
            <p className="font-medium text-main">{value.name}</p>
            {value.email && <p className="text-sm text-muted">{value.email}</p>}
            {value.phone && <p className="text-sm text-muted"><PhoneNumber number={value.phone} sourceContext="nalevering-search" stopPropagation /></p>}
            {value.street && (
              <p className="text-sm text-muted mt-1">
                {value.street}, {value.postalCode} {value.city}
              </p>
            )}
            {value.isNew && (
              <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-amber-100 text-amber-700">
                Nieuwe klant
              </span>
            )}
          </div>
          <button
            onClick={() => onChange(null)}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Wijzigen
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek op naam, email, telefoon of WO-nummer..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          autoFocus
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Search results */}
      {results.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
          {results.map((account) => (
            <button
              key={account.Id}
              onClick={() => selectAccount(account)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-sm text-main">{account.Name}</p>
              <div className="flex items-center gap-3 mt-0.5">
                {account.PersonEmail && (
                  <span className="text-xs text-muted">{account.PersonEmail}</span>
                )}
                {account.Phone && (
                  <PhoneNumber number={account.Phone} sourceContext="nalevering-search" className="text-xs text-muted" stopPropagation />
                )}
              </div>
              {account.BillingStreet && (
                <p className="text-xs text-muted mt-0.5">
                  {account.BillingStreet}, {account.BillingPostalCode} {account.BillingCity}
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {query.length >= 2 && !isSearching && results.length === 0 && (
        <p className="text-sm text-muted">Geen resultaten gevonden</p>
      )}

      {/* New customer toggle */}
      {!showNewForm ? (
        <button
          onClick={() => setShowNewForm(true)}
          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
        >
          + Nieuwe klant toevoegen
        </button>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-main">Nieuwe klant</h4>
          {potentialMatch && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
              <p className="font-medium text-amber-900">
                Mogelijk dezelfde klant: {potentialMatch.name}
                {potentialMatch.city ? ` — ${potentialMatch.city}` : ''}
              </p>
              <button
                type="button"
                onClick={useExistingMatch}
                className="mt-1 text-xs font-medium text-amber-900 underline hover:text-amber-950"
              >
                Gebruik deze klant
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Voornaam"
              value={newCustomer.firstName}
              onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="text"
              placeholder="Achternaam *"
              value={newCustomer.lastName}
              onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="email"
              placeholder="Email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="tel"
              placeholder="Telefoon"
              value={newCustomer.phone}
              onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <input
            type="text"
            placeholder="Straat + huisnummer"
            value={newCustomer.street}
            onChange={(e) => setNewCustomer({ ...newCustomer, street: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <div className="grid grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Postcode"
              value={newCustomer.postalCode}
              onChange={(e) => setNewCustomer({ ...newCustomer, postalCode: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="text"
              placeholder="Plaats"
              value={newCustomer.city}
              onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
              className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={submitNewCustomer}
              disabled={!newCustomer.lastName}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                newCustomer.lastName
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              )}
            >
              Klant selecteren
            </button>
            <button
              onClick={() => setShowNewForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
